import { api } from "@shared/lib/api";
import { BackednResponse } from "@shared/lib/types";
import {
  BriefCampaignInf,
  BriefCampaignInfs,
  IBankVerification,
  IBriefCampaignInfluencerSearchRequestV2,
  IbriefDynamicFacets,
  ICheckBankDetailsRequest,
  ICreateDownloadPayoutsQueue,
  ICreateGstPayout,
  ICreateInvoice,
  ICreateLedger,
  ICreateManualGstPayout,
  ICreateManualPayout,
  ICreateManualPayoutBank,
  ICreatePayout,
  ICreateVendorBilling,
  IEditInvoice,
  IEditLedger,
  IEditManualGstPayout,
  IEditManualPayout,
  IExportPaymentsRequest,
  IFinanceInfluencer,
  IGetCampaignInfluencersFacetsResponse,
  IGetPayoutFacetsResponse,
  IGetPayoutsRequest,
  IGetPayoutsResponse,
  IgetSearchPaymentsRequest,
  IgetSearchPaymentsResponse,
  IGetVendorBilling,
  IParseExportPaymentsResponse,
  IRegisterBankDetailsRequest,
  ISearchFacets,
  ISyncExportRequest,
} from "@shared/lib/types/briefCampains";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";

import { IGetCampaigns } from "@shared/lib/types/campaigns";
import {
  ExecutionStatusFilter,
  ExecutionStatusFilterEnum,
} from "@widgets/CampaignExecution/constants";
import {
  IAlterationPrerequisiteResponse,
  ICampaignAlterationRequestPayload,
  IUpdateTaskStartEndPayload,
  IUpdateTaskStartEndResponseItem,
} from "../types";

export class CampaignService {
  public static async getCampaigns(
    searchQuery?: string,
    campaignByBrandIds?: string[],
    limit?: number,
    pageNo?: number
  ): Promise<BackednResponse<IGetCampaigns>> {
    const params = new URLSearchParams();
    if (searchQuery) {
      params.append("searchQuery", searchQuery);
    }
    if (limit) {
      params.append("limit", limit.toString());
    }
    if (pageNo) {
      params.append("pageNo", pageNo.toString());
    }
    if (campaignByBrandIds && campaignByBrandIds.length > 0) {
      params.append("campaignByBrandIds", campaignByBrandIds.join("$$"));
    }
    const queryString = params.toString();
    const url = `campaign/${queryString ? `?${queryString}` : ""}`;

    const downloads = (await api
      .get(url)
      .json()) as BackednResponse<IGetCampaigns>;
    return downloads;
  }

  public static async getBriefCampaigns(
    params: IBriefCampaignInfluencerSearchRequestV2,
    status: ExecutionStatusFilter
  ): Promise<BackednResponse<BriefCampaignInfs>> {
    const url = `campaign/influencer/search/`;
    const downloads = (await api
      .post(url, {
        json: {
          ...params,
          sortByDynamicFields: params.sortByDynamicFields || {},
          ...(status === ExecutionStatusFilterEnum.ActionPending
            ? {
                anyTask: {
                  approvalPendingOn: ["Brand"],
                },
              }
            : status === ExecutionStatusFilterEnum.Completed
              ? {
                  anyLatestTask: {
                    statuses: ["Completed"],
                  },
                }
              : {}),
        },
      })
      .json()) as BackednResponse<BriefCampaignInfs>;
    return downloads;
  }

  public static async getVendorBilling(
    influencerId: string
  ): Promise<BackednResponse<IGetVendorBilling>> {
    const url = `billing/vendor/search?influencerId=${influencerId}`;
    const downloads = (await api
      .get(url)
      .json()) as BackednResponse<IGetVendorBilling>;
    return downloads;
  }

  public static async createVendorBilling(
    type: "Influencer" | "Agency",
    vendorBilling: ICreateVendorBilling,
    agencyId?: string,
    influencerId?: string
  ): Promise<BackednResponse<IGetVendorBilling>> {
    const url = `billing/vendor`;
    const downloads = (await api
      .put(url, {
        json: {
          ...(type === "Agency" && { agencyId }),
          ...(type === "Influencer" && { influencerId }),
          ...vendorBilling,
        },
      })
      .json()) as BackednResponse<IGetVendorBilling>;
    return downloads;
  }
  public static async attachVendorBilling(
    campaignInfluencerId: string,
    vendorBillingId: string,
    isEdit: boolean
  ) {
    const url = isEdit
      ? `campaign/influencer/re-attach-vendor-billing`
      : `campaign/influencer/attach-vendor-billing`;
    const downloads = await api
      .put(url, {
        json: {
          campaignInfluencerId,
          vendorBillingId,
        },
      })
      .json();
    return downloads;
  }

  public static async getFinanceInfluencer(
    campaignInfluencerId: string
  ): Promise<BackednResponse<IFinanceInfluencer>> {
    const url = `campaign/influencer/finance?campaignInfluencerId=${campaignInfluencerId}`;
    const downloads = (await api
      .get(url)
      .json()) as BackednResponse<IFinanceInfluencer>;
    return downloads;
  }

  public static async createLedger(ledger: ICreateLedger) {
    const url = `billing/vendor/ledger/manual`;
    const downloads = await api.put(url, { json: ledger }).json();
    return downloads;
  }

  public static async editLedger(ledger: IEditLedger) {
    const url = `billing/vendor/ledger/manual/`;
    const downloads = await api.post(url, { json: ledger }).json();
    return downloads;
  }

  public static async createManualPayout(payout: ICreateManualPayout) {
    const url = `billing/vendor/payments/manual`;
    const downloads = await api.put(url, { json: payout }).json();
    return downloads;
  }
  public static async editManualPayout(payout: IEditManualPayout) {
    const url = `billing/vendor/payments/manual`;
    const downloads = await api.post(url, { json: payout }).json();
    return downloads;
  }
  public static async createManualGstPayout(payout: ICreateManualGstPayout) {
    const url = `billing/vendor/payments/manual-gst`;
    const downloads = await api.put(url, { json: payout }).json();
    return downloads;
  }
  public static async editManualGstPayout(payout: IEditManualGstPayout) {
    const url = `billing/vendor/payments/manual-gst`;
    const downloads = await api.post(url, { json: payout }).json();
    return downloads;
  }

  public static async createManualPayoutBank(payout: ICreateManualPayoutBank) {
    const url = `billing/vendor/payments/bank-verification/manual`;
    const downloads = await api
      .put(url, {
        json: { ...payout, type: "Debit" },
      })
      .json();
    return downloads;
  }
  public static async createPayout(payout: ICreatePayout) {
    const url = `billing/vendor/payments/payouts`;
    const downloads = await api
      .post(url, {
        json: payout,
      })
      .json();
    return downloads;
  }

  public static async createGstPayout(payout: ICreateGstPayout) {
    const url = `billing/vendor/payments/payouts-gst`;
    const downloads = await api.post(url, { json: payout }).json();
    return downloads;
  }

  public static async createInvoice(invoice: ICreateInvoice) {
    const url = `billing/vendor/invoice`;
    const downloads = await api.put(url, { json: invoice }).json();
    return downloads;
  }

  public static async editInvoice(invoice: IEditInvoice) {
    const url = `billing/vendor/invoice`;
    const downloads = await api.post(url, { json: invoice }).json();
    return downloads;
  }
  public static async deleteInvoice(
    campaignInfluencerId: string,
    invoiceId: string
  ) {
    const url = `billing/vendor/invoice/`;
    const downloads = await api
      .delete(url, {
        json: {
          campaignInfluencerId,
          invoiceId,
        },
      })
      .json();
    return downloads;
  }
  public static async deletePayout(
    campaignInfluencerId: string,
    vendorPaymentId: string
  ) {
    const url = `billing/vendor/payments/manual`;
    const downloads = await api
      .delete(url, {
        json: {
          campaignInfluencerId,
          vendorPaymentId,
        },
      })
      .json();
    return downloads;
  }
  public static async deleteLedger(
    campaignInfluencerId: string,
    ledgerId: string
  ) {
    const url = `billing/vendor/ledger/manual`;
    const downloads = await api
      .delete(url, {
        json: {
          campaignInfluencerId,
          ledgerId,
        },
      })
      .json();
    return downloads;
  }
  public static async getBriefDynamicFacets(briefId: string) {
    const url = `brief/influencer-v2/facets/dynamic-fields?briefId=${briefId}`;
    const downloads = (await api
      .get(url)
      .json()) as BackednResponse<IbriefDynamicFacets>;
    return downloads;
  }

  public static async getBriefCampaignDynamicFacets(briefId: string) {
    const url = `campaign/influencer/facets/dynamic-fields?briefId=${briefId}`;
    const downloads = (await api
      .get(url)
      .json()) as BackednResponse<IbriefDynamicFacets>;
    return downloads;
  }

  public static async getBriefCampaignFacets(briefId: string) {
    const url = `campaign/influencer/facets`;
    const downloads = (await api
      .post(url, {
        json: {
          briefId,
        },
      })
      .json()) as BackednResponse<ISearchFacets>;
    return downloads;
  }

  public static async assignOperationsPoc(
    campaignInfluencerIds: string[],
    assignedToUserId: string
  ) {
    const url = `campaign/influencer/assign-operations-poc`;
    const downloads = await api
      .put(url, { json: { campaignInfluencerIds, assignedToUserId } })
      .json();
    return downloads;
  }

  public static async getPayouts(request: IGetPayoutsRequest) {
    const url = `billing/vendor/payments/search`;
    const downloads = (await api
      .post(url, { json: request })
      .json()) as BackednResponse<IGetPayoutsResponse>;
    return downloads;
  }
  public static async markPaymentsAsPaid(vendorPaymentIds: string[]) {
    const url = `billing/vendor/payments/mark-as-success`;
    const downloads = await api
      .post(url, { json: { vendorPaymentIds } })
      .json();
    return downloads;
  }
  public static async createDownloadPayoutsQueue(
    request: ICreateDownloadPayoutsQueue
  ) {
    const url = `download/vendorPayments`;
    const downloads = await api
      .post(url, { json: { ...request, isBankVerificationRequired: true } })
      .json();
    return downloads;
  }
  public static async getCampaignInfluencersFacets(briefId: string) {
    const url = `campaign/influencer/facets/tasks`;
    const downloads = (await api
      .post(url, {
        json: {
          briefId,
        },
      })
      .json()) as BackednResponse<IGetCampaignInfluencersFacetsResponse>;
    return downloads;
  }
  public static async downloadCampaignInfluencers(briefId: string) {
    const url = `download/campaignInfluencers`;
    const response = (await api
      .post(url, {
        json: {
          briefId,
        },
      })
      .json()) as BackednResponse<void>;
    return response;
  }

  public static async getCampaignInfluencer(
    campaignInfluencerId?: string,
    briefInfluencerId?: string
  ) {
    const url = `campaign/influencer/search/brief-campaign-influencer/`;
    const downloads = (await api
      .post(url, {
        json: {
          campaignInfluencerId,
          briefInfluencerId,
        },
      })
      .json()) as BackednResponse<{
      briefInfluencer: IBriefInfluencerV2;
      campaignInfluencer: BriefCampaignInf;
      finalBrandCost: number;
      finalInfluencerCost: number;
    }>;
    return downloads;
  }

  public static async createAlterationRequest(
    payload: ICampaignAlterationRequestPayload
  ) {
    const url = `campaign/influencer/alteration/create-request`;
    const response = await api
      .post(url, {
        json: payload,
      })
      .json();
    return response;
  }

  public static async alterationPrerequisite(
    payload: ICampaignAlterationRequestPayload
  ) {
    const url = `campaign/influencer/alteration/pre-check`;
    const response = (await api
      .put(url, {
        json: payload,
      })
      .json()) as BackednResponse<IAlterationPrerequisiteResponse>;
    return response;
  }
  public static async verifyPan(pan: string) {
    const url = `billing/vendor/pan-verification`;
    const response = (await api
      .put(url, {
        json: { panNumber: pan },
      })
      .json()) as BackednResponse<{ pan: string; status: "Invalid" | "Valid" }>;
    return response;
  }
  public static async syncVendorPaymentsExport(request: ISyncExportRequest) {
    const url = `billing/vendor/payments/bank-verification/export/sync`;
    const response = await api.post(url, { json: request }).json();
    return response;
  }

  public static async cancelVendorPaymentsExport(vendorPaymentId: string) {
    const url = `billing/vendor/payments/bank-verification`;
    const response = await api
      .delete(url, { json: { vendorPaymentId } })
      .json();
    return response;
  }

  public static async cancelVendorPaymentsBankVerificationExport(
    vendorPaymentBankExportId: string
  ) {
    const url = `billing/vendor/payments/bank-verification/export`;
    const response = await api
      .delete(url, { json: { vendorPaymentBankExportId } })
      .json();
    return response;
  }

  public static async getVendorPaymentsBankVerificationExports(
    request: IgetSearchPaymentsRequest
  ) {
    const url = `billing/vendor/payments/bank-verification/export/search`;
    const response = (await api
      .post(url, { json: request })
      .json()) as BackednResponse<IgetSearchPaymentsResponse>;
    return response;
  }

  public static async parseVendorPaymentsBankVerificationExport(request: {
    vendorPaymentBankExportId: string;
    csv: string;
  }) {
    const url = `billing/vendor/payments/bank-verification/export/parse`;
    const response = (await api
      .post(url, { json: request })
      .json()) as BackednResponse<IParseExportPaymentsResponse[]>;
    return response;
  }

  public static async getVendorPaoutsFacets(
    request: Omit<IGetPayoutsRequest, "briefIds">
  ) {
    const url = `billing/vendor/payments/facets`;
    const response = (await api
      .post(url, { json: request })
      .json()) as BackednResponse<IGetPayoutFacetsResponse>;
    return response;
  }

  public static async createVendorPaymentsBankVerificationExport(
    request: IExportPaymentsRequest
  ) {
    const url = `billing/vendor/payments/bank-verification/export`;
    const response = await api.post(url, { json: request }).json();
    return response;
  }

  public static async checkBankDetails(
    request: ICheckBankDetailsRequest
  ): Promise<BackednResponse<IBankVerification>> {
    const url = `billing/vendor/payments/bank-verification/check-bank-details`;
    const response = (await api
      .post(url, { json: request })
      .json()) as BackednResponse<IBankVerification>;
    return response;
  }

  public static async registerBankDetails(
    request: IRegisterBankDetailsRequest
  ) {
    const url = `billing/vendor/payments/bank-verification/register-bank-details`;
    const response = await api.post(url, { json: request }).json();
    return response;
  }

  public static async updateTaskStartEnd(
    payload: IUpdateTaskStartEndPayload
  ): Promise<BackednResponse<IUpdateTaskStartEndResponseItem[]>> {
    const url = `campaign/influencer/task/start-end`;
    const response = (await api
      .post(url, { json: payload })
      .json()) as BackednResponse<IUpdateTaskStartEndResponseItem[]>;
    return response;
  }
}
